import { useEffect, useState } from 'react'
import { RowToggle } from '../wiki/RowToggle'
import { PickerFoot } from '../PickerFoot'
import { messageOf } from '../../lib/errors'
import { findPeople, type PersonOption } from '../../lib/stars'
import {
  ROOM_TIMEZONES,
  addRoomManager,
  createStockRoom,
  editStockRoom,
  loadRoomManagers,
  removeRoomManager,
  setStockRoomActive,
  type RoomManager,
  type StockRoom,
} from '../../lib/shop'

/**
 * Stock rooms, and who runs them. REC-10's missing half.
 *
 * ── A ROOM IS AN ADDRESS AND IT IS TYPED ────────────────────────────────────
 *
 * David corrected this explicitly on 13 Sep: the location field on a person
 * says where they are BASED; a stock room says where a box SITS. Seeding one
 * from the other puts swag in places with no shelf. So there is no "pick an
 * office" control here and there must not be one — street and city are NOT NULL
 * in the schema because a room somebody cannot find is not a room.
 *
 * ── THE TIMEZONE IS REFUSED, NOT DEFAULTED ──────────────────────────────────
 *
 * Rule 9, and 064 is deliberately stricter than 023's notification chain: that
 * one falls back to a default because BambooHR sends whatever it sends, this
 * one is typed by an admin who can be told they are wrong. The select has no
 * blank option for that reason — a handover happens at a place, at a local
 * hour, and a zone that is wrong by eight hours says the shop opens in the
 * middle of the night.
 *
 * ── CLOSING A ROOM IS REVERSIBLE AND DELETING ONE IS NOT OFFERED ────────────
 *
 * `set_stock_room_active` is the whole of it. There is no delete, because a
 * closed room still has orders and handovers pointing at it and 041's rule
 * applies: the soft path preserves what people saw. A room with stock still on
 * its shelves can be closed — that is a real state and the manager knows it
 * better than a guard would.
 */
export function StockRooms({
  rooms,
  tenantId,
  canAdmin,
  openRoomId = null,
  onOpenRoom,
  onChanged,
}: {
  rooms: StockRoom[]
  tenantId: string
  /** shop.admin. Creating rooms and appointing managers is admin-only (064). */
  canAdmin: boolean
  /**
   * Which room is expanded — CONTROLLED by the route, as PeopleTable's panel
   * is. See the note in ShopItems: a disclosure that only opens on a click is
   * one no assertion can reach, so every check written against its contents
   * passes whether or not they are right.
   */
  openRoomId?: string | null
  onOpenRoom?: (roomId: string | null) => void
  onChanged: () => void
}) {
  const [managers, setManagers] = useState<RoomManager[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  /**
   * Which room's address is open for editing. Separate from `adding`, and the
   * editor is its own component rather than a second mode of NewRoom — 059's
   * rule for the notice editor: a form that is sometimes Create and sometimes
   * Save is how a second copy of the thing gets published.
   */
  const [editing, setEditing] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    loadRoomManagers()
      .then((m) => live && setManagers(m))
      .catch((e) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [rooms])

  async function run(key: string, fn: () => Promise<unknown>) {
    setBusy(key)
    setError(null)
    try {
      await fn()
      setManagers(await loadRoomManagers())
      onChanged()
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="shoprooms">
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {rooms.length === 0 && (
        <p className="empty">
          No stock rooms yet. A room is an address, not an office you pick from a
          list — type where the shelf actually is.
        </p>
      )}

      <ul className="shoprooms__list">
        {rooms.map((room) => {
          const mine = managers.filter((m) => m.stockRoomId === room.id)
          const open = openRoomId === room.id
          return (
            <li key={room.id} className="shoprooms__item">
              <div className="shoprooms__main">
                <button
                  type="button"
                  className="shoprooms__name"
                  aria-expanded={open}
                  onClick={() => onOpenRoom?.(open ? null : room.id)}
                >
                  <span className="shoprooms__title">{room.name}</span>
                  <span className="shoprooms__addr">
                    {room.street}, {room.city}
                    {room.region ? `, ${room.region}` : ''}
                    {room.postalCode ? ` ${room.postalCode}` : ''}, {room.country}
                  </span>
                </button>
                <span className="shoprooms__meta">
                  {/* The zone is shown, not hidden behind the edit form. It is
                      what decides when a handover is "today" for the person on
                      the shelf, and it is the field most likely to be wrong. */}
                  {/* 14 Oct: "Vancouver time" in body type rather than
                      AMERICA/VANCOUVER as a shouting tag; the full zone is the
                      tooltip and the edit form. */}
                  <span className="shoprooms__tz" title={`${room.timezone} — local time at this room (rule 9)`}>
                    {zoneCity(room.timezone)} time
                  </span>
                  {!room.isActive && <span className="pill pill--draft">Closed</span>}
                  {room.iManage && <span className="pill">You run this</span>}
                </span>
              </div>

              {open && (
                <div className="shoprooms__panel">
                  <h4 className="shoprooms__subhead">Who runs it</h4>
                  {mine.length === 0 ? (
                    <p className="empty">
                      {canAdmin
                        ? 'Nobody is listed. A room with no manager has nobody who can count its shelves.'
                        : // 064's policy is "your own rows, or shop.admin", so
                          // this is not an empty room — it is a list this
                          // person may not read. Saying "nobody" would be the
                          // 042 failure: a fact about the caller rendered as a
                          // fact about the world.
                          'Only a shop administrator can see the full list of managers.'}
                    </p>
                  ) : (
                    <ul className="shoprooms__mgrs">
                      {mine.map((m) => (
                        <li key={m.personId}>
                          <span>{m.displayName}</span>
                          {canAdmin && (
                            <button
                              type="button"
                              className="btn btn--sm"
                              disabled={busy === `rm-${m.personId}`}
                              onClick={() =>
                                run(`rm-${m.personId}`, () => removeRoomManager(room.id, m.personId))
                              }
                            >
                              Remove
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}

                  {canAdmin && (
                    <AddManager
                      busy={busy === `add-${room.id}`}
                      onAdd={(personId) => run(`add-${room.id}`, () => addRoomManager(room.id, personId))}
                    />
                  )}

                  {/* THE ADDRESS IS TYPED, SO IT CAN BE WRONG — and until 071
                      nothing could fix it. `code` is not offered: it is the
                      stable key, and an identifier you may rewrite is one that
                      stops identifying (067). */}
                  {canAdmin && (
                    <>
                      <h4 className="shoprooms__subhead">Where it is</h4>
                      {editing === room.id ? (
                        <EditRoom
                          room={room}
                          onCancel={() => setEditing(null)}
                          onSaved={() => {
                            setEditing(null)
                            onChanged()
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="btn btn--sm"
                          onClick={() => setEditing(room.id)}
                        >
                          Edit address
                        </button>
                      )}
                    </>
                  )}

                  {canAdmin && (
                    <div className="shoprooms__state">
                      <RowToggle
                        label={`Whether ${room.name} is open`}
                        value={room.isActive ? 'open' : 'closed'}
                        options={[
                          { key: 'open', text: 'Open', title: 'Orders may name this room' },
                          { key: 'closed', text: 'Closed', title: 'Kept, with its history — no deletion' },
                        ]}
                        busy={busy === `state-${room.id}`}
                        onPick={(k) =>
                          run(`state-${room.id}`, () => setStockRoomActive(room.id, k === 'open'))
                        }
                      />
                    </div>
                  )}
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {canAdmin &&
        (adding ? (
          <NewRoom
            tenantId={tenantId}
            onCancel={() => setAdding(false)}
            onCreated={() => {
              setAdding(false)
              onChanged()
            }}
          />
        ) : (
          <button type="button" className="btn" onClick={() => setAdding(true)}>
            Add a stock room
          </button>
        ))}
    </div>
  )
}

/**
 * ONE person search, shared with the @ menu and the give form (059, 062c).
 * A second one here is what REC-2 warns against: two searches disagree about
 * who exists the first time either changes.
 *
 * Business unit AND job title are both rendered, and that was measured rather
 * than reasoned (062c): unit separates the two Kenneth Taylors and not the two
 * James Millers; title separates the Millers and not the Taylors.
 */
function AddManager({ busy, onAdd }: { busy: boolean; onAdd: (personId: string) => void }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<PersonOption[]>([])
  /** What matched, from the server (088). REC-54. */
  const [total, setTotal] = useState(0)

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([])
      return
    }
    let live = true
    const t = setTimeout(() => {
      findPeople(query)
        .then((page) => {
          if (!live) return
          setResults(page.people)
          // REC-54. Eight of however many matched, and appointing the wrong
          // manager because the right one was never on the list is the most
          // expensive version of this bug on any of the four screens.
          setTotal(page.total)
        })
        .catch(() => undefined)
    }, 200)
    return () => {
      live = false
      clearTimeout(t)
    }
  }, [query])

  return (
    <div className="shoprooms__add">
      <label className="admin__field">
        <span>Appoint a manager</span>
        <input
          type="search"
          value={query}
          placeholder="Name"
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      {results.length > 0 && (
        <ul className="shoprooms__results">
          {results.map((p) => (
            <li key={p.personId}>
              <button
                type="button"
                className="btn btn--sm"
                disabled={busy}
                onClick={() => {
                  onAdd(p.personId)
                  setQuery('')
                  setResults([])
                }}
              >
                {p.displayName}
                <span className="shoprooms__who">
                  {[p.businessUnit, p.jobTitle].filter(Boolean).join(' · ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* REC-54. Below the results, so it describes the search rather than
          interrupting it. */}
      <PickerFoot shown={results.length} total={total} hasQuery />
    </div>
  )
}

/**
 * 071 — the address editor, opened in place under the room it is about.
 *
 * NOT a second mode of NewRoom (059's rule for the notice editor: a form that
 * is sometimes Create and sometimes Save is how a second copy gets published),
 * and not a modal — the thing being corrected is the line directly above it,
 * and covering that up to retype it is how a "fix" lands on the wrong room.
 *
 * IT SENDS EVERY FIELD, ALWAYS. `edit_stock_room` is a REPLACE with no
 * defaults (049), so a field this form omitted would be cleared rather than
 * left — which means the form must be prefilled from the room and must keep
 * sending what it read back. That is also why the fields are seeded with
 * useState initialisers from `room` and the component is keyed on the room id
 * by its caller's conditional: a stale prefill would quietly overwrite one
 * room's address with another's.
 *
 * `code` is absent, deliberately. See the lib note.
 */
export function EditRoom({
  room,
  onCancel,
  onSaved,
}: {
  room: StockRoom
  onCancel: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(room.name)
  const [street, setStreet] = useState(room.street)
  const [city, setCity] = useState(room.city)
  const [region, setRegion] = useState(room.region ?? '')
  const [postal, setPostal] = useState(room.postalCode ?? '')
  const [country, setCountry] = useState(room.country)
  const [timezone, setTimezone] = useState(room.timezone)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = name.trim() && street.trim() && city.trim() && country.trim()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await editStockRoom({
        roomId: room.id,
        name: name.trim(),
        street: street.trim(),
        city: city.trim(),
        region: region.trim() || null,
        postalCode: postal.trim() || null,
        country: country.trim(),
        timezone,
      })
      onSaved()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="shoprooms__new" onSubmit={submit}>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      {/* The code is shown and not editable. Hiding it would leave somebody
          wondering where it went; an input would invite them to change it. */}
      <p className="admin__hint">
        Short code <code>{room.code}</code> — fixed. Orders and stock rows hang
        off this room, so a code that can be rewritten is one that stops
        identifying it. To change it, close this room and open another.
      </p>
      <div className="admin__fields">
        <label className="admin__field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Street</span>
          <input value={street} onChange={(e) => setStreet(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>City</span>
          <input value={city} onChange={(e) => setCity(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Region (optional)</span>
          <input value={region} onChange={(e) => setRegion(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Postcode (optional)</span>
          <input value={postal} onChange={(e) => setPostal(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Country</span>
          <input value={country} onChange={(e) => setCountry(e.target.value)} />
        </label>
        <label className="admin__field">
          <span>Local time zone</span>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {ROOM_TIMEZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="admin__hint">
        Clearing the region or postcode here empties it. Every field is sent, so
        what you see is what the room becomes.
      </p>
      <div className="shoprooms__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={!ready || busy}>
          {busy ? 'Saving…' : 'Save address'}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

function NewRoom({
  tenantId,
  onCancel,
  onCreated,
}: {
  tenantId: string
  onCancel: () => void
  onCreated: () => void
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [street, setStreet] = useState('')
  const [city, setCity] = useState('')
  const [region, setRegion] = useState('')
  const [postal, setPostal] = useState('')
  const [country, setCountry] = useState('')
  const [timezone, setTimezone] = useState('America/Vancouver')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = code.trim() && name.trim() && street.trim() && city.trim() && country.trim()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      await createStockRoom({
        tenantId,
        code: code.trim(),
        name: name.trim(),
        street: street.trim(),
        city: city.trim(),
        region: region.trim() || null,
        postalCode: postal.trim() || null,
        country: country.trim(),
        timezone,
      })
      onCreated()
    } catch (err) {
      setError(messageOf(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="shoprooms__new" onSubmit={submit}>
      <h4 className="shoprooms__subhead">New stock room</h4>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}
      <div className="admin__fields">
        <label className="admin__field">
          <span>Short code</span>
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="burnaby" />
        </label>
        <label className="admin__field">
          <span>Name</span>
          {/* A place name, not "X Store Room" — David, 14 Sep. The name is what
              somebody picks from a list, and repeating the same two words
              sixteen times makes the part that differs the hardest to read. It
              is also NOT the city: the San Francisco room is in Richmond CA and
              Rye is in Iden. */}
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Burnaby" />
        </label>
        <label className="admin__field">
          <span>Street</span>
          <input value={street} onChange={(e) => setStreet(e.target.value)} placeholder="8327 Eastlake Drive" />
        </label>
        <label className="admin__field">
          <span>City</span>
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Burnaby" />
        </label>
        <label className="admin__field">
          <span>Region (optional)</span>
          <input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="BC" />
        </label>
        {/* Optional, and it stays optional: some yards have no postcode on any
            record we hold. A required field there is a rule that makes the
            truthful row impossible, so somebody invents one. */}
        <label className="admin__field">
          <span>Postcode (optional)</span>
          <input value={postal} onChange={(e) => setPostal(e.target.value)} placeholder="V5A 4W2" />
        </label>
        <label className="admin__field">
          <span>Country</span>
          <input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="Canada" />
        </label>
        <label className="admin__field">
          <span>Local time zone</span>
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {ROOM_TIMEZONES.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="admin__hint">
        The address is what somebody reads before they order, so they know their
        jacket sits in Burnaby and not Lima. It is typed, never taken from
        anybody's office — a stock room is where a box sits, not where a person
        is based.
      </p>
      <div className="shoprooms__actions">
        <button type="submit" className="btn btn--primary btn--sm" disabled={!ready || busy}>
          {busy ? 'Adding…' : 'Add room'}
        </button>
        <button type="button" className="btn btn--sm" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  )
}

/** "America/Argentina/Buenos_Aires" → "Buenos Aires": the zone's own city. */
function zoneCity(zone: string): string {
  return (zone.split('/').pop() ?? zone).replace(/_/g, ' ')
}
